import React from 'react';

import { PaginationConfigProps, PaginationProps } from './paginationModels';
import { paginationItemsPerPageConfig } from '../../config/appConfig';
import {
  PaginationArrowLeftIcon,
  PaginationArrowRightIcon,
  TriangleSmallIcon,
} from '../../assets';

const Pagination: React.FC<PaginationProps> = ({
  paginationConfig,
  setPaginationConfig,
  totalPaginationPages,
  totalItems,
}) => {
  const currentPage = paginationConfig?.currentPage || 1;
  const itemsPerPage = paginationConfig?.itemsPerPage;

  let startItem = 1;
  let endItem = totalItems;
  let isPageNumberOutOfRange = false;

  if (totalItems === 0) {
    startItem = 0;
    endItem = 0;
  } else if (totalItems <= itemsPerPage) {
    endItem = totalItems;
  } else {
    if (currentPage === 1) {
      endItem = itemsPerPage;
    } else {
      startItem = itemsPerPage * (currentPage - 1) + 1;
      endItem = Math.min(totalItems, startItem + itemsPerPage - 1);
    }
  }

  const displayRange = `${startItem}-${endItem}`;

  const PageNumbers = [...new Array(totalPaginationPages)].map((_, index) => {
    const pageNumber = index + 1;
    const isPageNumberFirst = pageNumber === 1;
    const isPageNumberLast = pageNumber === totalPaginationPages;
    const isCurrentPageWithinTwoPageNumbers =
      Math.abs(pageNumber - currentPage) <= 2;

    if (
      isPageNumberFirst ||
      isPageNumberLast ||
      isCurrentPageWithinTwoPageNumbers
    ) {
      isPageNumberOutOfRange = false;
      return (
        <li
          key={index}
          className={`pagination-item ${currentPage === index + 1 ? 'active' : ''}`}
          onClick={() => goToPage(index + 1)}
        >
          <div className="pagination-item-container pagination-icon">
            <span>{index + 1}</span>
          </div>
        </li>
      );
    }

    if (!isPageNumberOutOfRange) {
      isPageNumberOutOfRange = true;
      return (
        <li key={index} className="pagination-item">
          <div className="pagination-ellipsis">...</div>
        </li>
      );
    }

    return null;
  });

  const handleIncreaseItemsPerPage = () => {
    const currentIndex = paginationItemsPerPageConfig.indexOf(itemsPerPage);
    if (currentIndex < paginationItemsPerPageConfig.length - 1) {
      const newItemsPerPage = paginationItemsPerPageConfig[currentIndex + 1];
      setPaginationConfig((prevState: PaginationConfigProps) => ({
        ...prevState,
        itemsPerPage: newItemsPerPage,
      }));
    }
  };

  const handleDecreaseItemsPerPage = () => {
    const currentIndex = paginationItemsPerPageConfig.indexOf(itemsPerPage);
    if (currentIndex > 0) {
      const newItemsPerPage = paginationItemsPerPageConfig[currentIndex - 1];
      setPaginationConfig((prevState: PaginationConfigProps) => ({
        ...prevState,
        itemsPerPage: newItemsPerPage,
      }));
    }
  };

  const goToPage = (pageNumber: number) => {
    setPaginationConfig((prevState: PaginationConfigProps) => ({
      ...prevState,
      currentPage: pageNumber,
    }));
  };

  const goToNextPage = () => {
    if (currentPage < totalPaginationPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  return (
    <div className="d-flex-row-space-between w-100 ">
      <span className="f-roboto-m-primary fs-0_875rem ">
        {displayRange} of {totalItems} items
      </span>
      <ul className="d-flex-row-space-between list-style-none">
        <li className="pagination-item" onClick={goToPreviousPage}>
          <button
            className="pagination-item-container pagination-icon"
            disabled={currentPage === 1}
          >
            <PaginationArrowLeftIcon />
          </button>
        </li>
        <div className="d-flex-row p-0-1rem">{PageNumbers}</div>
        <li className="pagination-item" onClick={goToNextPage}>
          <button className="pagination-item-container pagination-icon">
            <PaginationArrowRightIcon className="" />
          </button>
        </li>
        <div className="d-flex-row ml-1rem h-2_375rem b-1 b-radius-0_625rem ">
          <span className="p-0-0_625rem f-roboto-secondary fs-0_875rem">
            {itemsPerPage}
          </span>
          <div className="d-flex-center bl-1 h-2_375rem w-1_5rem">
            <div
              className="d-flex-end-center w-100 h-100 icon-hovered"
              onClick={handleIncreaseItemsPerPage}
            >
              <TriangleSmallIcon className="t-rotate-180 m-0_188rem" />
            </div>
            <div
              className="d-flex-start-center w-100 h-100 icon-hovered"
              onClick={handleDecreaseItemsPerPage}
            >
              <TriangleSmallIcon className="m-0_188rem " />
            </div>
          </div>
        </div>
      </ul>
    </div>
  );
};

export default Pagination;
